import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { chatApi } from "../../api/chat";
import type { ChatAttachment, ChatChannel, ChatMessage, ChatPerson } from "../../api/chat";
import { useAuth } from "../../auth/AuthContext";
import { useChat } from "../../components/chat/useChatConnection";
import { useConfirm } from "../../components/ConfirmDialog";
import { useToastError } from "../../components/Toast";
import Ic from "../../components/icons.jsx";
import { dayKey, layoutRows } from "./chatFormat";
import { MessageInput } from "./MessageInput";
import { Lightbox, MessageRow } from "./MessageRow";

type Pending = { id: string; body: string; files: File[]; createdAt: string; failed: boolean };

/** Replies in time order, the given ones in place of any with the same id. */
function merge(list: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map(list.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

/**
 * A message's thread, beside the channel as in Slack: the message on top, its replies under it, and a box to answer
 * in. On a narrow screen it takes the channel's place. It follows the stream: a reply, a correction or a deletion
 * shows at once, and the thread closes if its message is deleted. `found`, a reply a search led to, opens in view and
 * lights up for a moment. The head says whether the user follows the thread, and starts or stops it.
 */
export function ThreadPanel({ channel, parentId, people, narrow, onClose, found }: {
  channel: ChatChannel;
  parentId: string;
  people: ChatPerson[];
  narrow: boolean;
  onClose: () => void;
  found?: string | null;
}) {
  const { t } = useTranslation();
  const chat = useChat();
  const { me, subscribe } = chat;
  const peopleById = chat.people;
  const moderator = useAuth().hasPermission("chat:moderate");
  const confirm = useConfirm();
  const toastError = useToastError();
  const [parent, setParent] = useState<ChatMessage | null>(null);
  const [replies, setReplies] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState<Pending[]>([]);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [palette, setPalette] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<ChatAttachment | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const placed = useRef(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [following, setFollowing] = useState<boolean | null>(null);

  const load = useCallback(() => {
    chatApi.thread(parentId)
      .then(([head, ...rest]) => {
        setParent(head);
        setReplies(rest);
      })
      .catch(() => setFailed(true));
  }, [parentId]);

  useEffect(load, [load]);

  useEffect(() => subscribe((ev) => {
    if ((ev.type === "message" || ev.type === "updated" || ev.type === "followed") && ev.message.id === parentId) {
      setParent(ev.message);
    } else if (ev.type === "message" && ev.message.parentId === parentId) {
      const m = ev.message;
      setReplies((list) => merge(list, [m]));
      if (m.authorId === me) {
        setPending((list) => {
          const i = list.findIndex((p) => p.body === m.body && !p.failed);
          return i < 0 ? list : [...list.slice(0, i), ...list.slice(i + 1)];
        });
      }
    } else if (ev.type === "updated" && ev.message.parentId === parentId) {
      setReplies((list) => list.map((m) => (m.id === ev.message.id ? ev.message : m)));
    } else if (ev.type === "deleted") {
      if (ev.messageId === parentId) onClose();
      else if (ev.parentId === parentId) setReplies((list) => list.filter((m) => m.id !== ev.messageId));
    } else if (ev.type === "ready") {
      load();
    }
  }), [subscribe, parentId, me, onClose, load]);

  useEffect(() => {
    if (!palette) return;
    const close = (e: Event) => {
      if (!(e.target as HTMLElement).closest(".chat-palette, .chat-palette-toggle")) setPalette(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [palette]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && lightbox) setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const shown = useMemo<ChatMessage[]>(() => [
    ...replies,
    ...pending.map((p) => ({
      id: p.id, channelId: channel.id, authorId: me, body: p.body, mentions: [], createdAt: p.createdAt, editedAt: null,
      reactions: [], attachments: [], parentId, parentAuthorId: parent?.authorId ?? null, replyCount: 0,
      lastReplyAt: null, replyUsers: [], followers: [],
    })),
  ], [replies, pending, channel.id, me, parentId, parent?.authorId]);

  const rows = useMemo(
    () => layoutRows(shown, { me, newAfter: null, now: new Date(), today: t("chat.today", "Today"), yesterday: t("chat.yesterday", "Yesterday") }),
    [shown, me, t],
  );

  // New replies keep the thread at its bottom for whoever was reading there; a found reply opens in the middle.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !parent) return;
    if (!placed.current) {
      placed.current = true;
      const row = found ? el.querySelector<HTMLElement>(`[data-message="${found}"]`) : null;
      if (row) {
        el.scrollTop += row.getBoundingClientRect().top - el.getBoundingClientRect().top - (el.clientHeight - row.offsetHeight) / 2;
        atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        setFlash(found ?? null);
        return;
      }
    }
    if (atBottom.current) el.scrollTop = el.scrollHeight;
  }, [rows, parent, found]);

  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), 3000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  const keepBottom = useCallback(() => {
    const el = scroller.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, []);

  async function send(body: string, files: File[], retryOf?: string) {
    const p: Pending = { id: `sending-${Date.now()}-${Math.random().toString(36).slice(2)}`, body, files,
      createdAt: new Date().toISOString(), failed: false };
    atBottom.current = true;
    setPending((list) => [...list.filter((x) => x.id !== retryOf), p]);
    try {
      const m = files.length > 0 ? await chatApi.replyWithFiles(parentId, body, files) : await chatApi.reply(parentId, body);
      setPending((list) => list.filter((x) => x.id !== p.id));
      setReplies((list) => merge(list, [m]));
    } catch {
      setPending((list) => list.map((x) => (x.id === p.id ? { ...x, failed: true } : x)));
    }
  }

  // The button turns at once; the answer, or the stream from another tab, brings the thread's real followers.
  const follows = following ?? Boolean(parent?.followers.includes(me));
  async function follow() {
    const next = !follows;
    setFollowing(next);
    try {
      setParent(await chatApi.follow(parentId, next));
    } catch (e) {
      toastError(e);
    }
    setFollowing(null);
  }

  async function react(message: ChatMessage, emoji: string) {
    setPalette(null);
    try {
      const updated = await chatApi.react(message.id, emoji);
      if (updated.id === parentId) setParent(updated);
      else setReplies((list) => list.map((m) => (m.id === updated.id ? updated : m)));
    } catch (e) {
      toastError(e);
    }
  }

  async function saveEdit(message: ChatMessage, body: string) {
    setEditing(null);
    if (body === message.body) return;
    try {
      const updated = await chatApi.edit(message.id, body);
      if (updated.id === parentId) setParent(updated);
      else setReplies((list) => list.map((m) => (m.id === updated.id ? updated : m)));
    } catch (e) {
      toastError(e);
    }
  }

  async function remove(message: ChatMessage) {
    const whole = message.id === parentId;
    const ok = await confirm({
      title: t("chat.deleteTitle", "Delete this message?"),
      message: whole
        ? t("chat.deleteWithThread", "It disappears for everyone in the channel, together with its thread.")
        : t("chat.deleteMessage", "It disappears for everyone in the channel."),
      confirmLabel: t("chat.delete", "Delete"),
      danger: true,
    });
    if (!ok) return;
    try {
      await chatApi.remove(message.id);
      if (whole) onClose();
      else setReplies((list) => list.filter((m) => m.id !== message.id));
    } catch (e) {
      toastError(e);
    }
  }

  function row(m: ChatMessage, head: boolean, pendingOf?: Pending) {
    const mine = m.authorId === me;
    return (
      <MessageRow
        key={m.id}
        message={m}
        head={head}
        person={peopleById.get(m.authorId)}
        online={chat.online.has(m.authorId)}
        people={peopleById}
        me={me}
        mine={mine}
        canDelete={mine || moderator}
        sending={pendingOf ? (pendingOf.failed ? "failed" : "sending") : null}
        editing={editing === m.id}
        selected={false}
        paletteOpen={palette === m.id}
        peopleList={people}
        onClick={() => {}}
        onReact={(emoji) => react(m, emoji)}
        onPalette={() => setPalette((x) => (x === m.id ? null : m.id))}
        onEdit={() => setEditing(m.id)}
        onCancelEdit={() => setEditing(null)}
        onSaveEdit={(body) => saveEdit(m, body)}
        onDelete={() => remove(m)}
        onRetry={() => pendingOf && send(pendingOf.body, pendingOf.files, pendingOf.id)}
        onDiscard={() => setPending((list) => list.filter((x) => x.id !== m.id))}
        pendingFiles={pendingOf?.files}
        onOpenImage={setLightbox}
        onMediaLoad={keepBottom}
        found={flash === m.id}
      />
    );
  }

  const pendingById = new Map(pending.map((p) => [p.id, p]));

  return (
    <aside className="chat-thread" aria-label={t("chat.thread", "Thread")}>
      <header className="chat-thread-head">
        {narrow && (
          <button type="button" className="chat-tool" onClick={onClose} aria-label={t("chat.closeThread", "Close thread")}>
            <Ic.arrowLeft />
          </button>
        )}
        <div className="chat-thread-title">
          <h2>{t("chat.thread", "Thread")}</h2>
          <span>{channel.directWith ? peopleById.get(channel.directWith)?.name : `#${channel.name}`}</span>
        </div>
        {parent && (
          <button type="button" className={follows ? "chat-follow on" : "chat-follow"} onClick={() => void follow()}
            title={follows ? t("chat.unfollowHint", "You follow this thread. Click to stop getting its new replies")
              : t("chat.followHint", "New replies in this thread will come to your notifications")}>
            <Ic.bell />
            <span>{follows ? t("chat.following", "Following") : t("chat.follow", "Follow")}</span>
          </button>
        )}
        {!narrow && (
          <button type="button" className="chat-tool" onClick={onClose}
            title={t("chat.closeThread", "Close thread")} aria-label={t("chat.closeThread", "Close thread")}>
            <Ic.x />
          </button>
        )}
      </header>
      <div
        className="chat-thread-scroll"
        ref={scroller}
        onScroll={() => {
          const el = scroller.current;
          if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        {failed && <p className="chat-error chat-pad">{t("chat.loadFailed", "The messages could not be loaded.")}</p>}
        {!parent && !failed && <p className="chat-muted chat-pad">{t("chat.loading", "Loading…")}</p>}
        {parent && (
          <>
            {row(parent, true)}
            <div className="chat-thread-divider">
              <span>{t("chat.replies", "{{count}} replies", { count: replies.length })}</span>
            </div>
            {rows.map((r, i) => {
              // Replies from the message's own day need no day line under it.
              if (r.kind === "day" && i === 0 && r.key === `day-${dayKey(parent.createdAt)}`) return null;
              if (r.kind === "day") return <div key={r.key} className="chat-day"><span>{r.label}</span></div>;
              if (r.kind === "new") return null;
              return row(r.message, r.head, pendingById.get(r.message.id));
            })}
          </>
        )}
      </div>
      {channel.member && (
        <div className="chat-compose">
          <MessageInput
            key={parentId}
            variant="compose"
            people={people}
            placeholder={t("chat.replyPlaceholder", "Reply…")}
            onSubmit={(body, files) => void send(body, files)}
            focusOnOpen={!narrow}
          />
        </div>
      )}
      {lightbox && <Lightbox file={lightbox} onClose={() => setLightbox(null)} />}
    </aside>
  );
}
